'use client';

import React from 'react';
import Link from 'next/link';
import { m } from 'motion/react';
import { cx } from 'styled-system/css';

import useLocalizedHref, { isExternalHref } from '@shaojiang/hooks/useLocalizedHref';

import { MOTION_TAGS, type MotionTag } from './tags';
import { useDesignMotion } from './useDesignMotion';

const MotionLink = m.create(Link);

type DomProps = Omit<
  React.HTMLAttributes<HTMLElement>,
  'onAnimationStart' | 'onDrag' | 'onDragStart' | 'onDragEnd'
>;

export interface LiftProps extends DomProps {
  /** Renders a (locale-aware) link; external links open in a new tab */
  href?: string;
  as?: MotionTag;
  /** Lift less, e.g. for list rows (multiplies the design's lift) */
  amount?: number;
  /** Slide sideways instead of up (list rows) */
  axis?: 'y' | 'x';
}

/**
 * Hover and press feedback for cards and tiles: lifts (and, in Storybook, tilts) with the
 * design's spring. Adds the `group` class so children can react with `.group:hover` styles
 * (image zoom, arrow badge, cover sun).
 */
export function Lift({
  href,
  as = 'div',
  amount = 1,
  axis = 'y',
  className,
  children,
  ...rest
}: LiftProps) {
  const motion = useDesignMotion();
  const localize = useLocalizedHref();

  const distance = motion.hover.y * amount;
  const hover = {
    ...(axis === 'y' ? { y: distance } : { x: -distance }),
    rotate: motion.hover.rotate * amount * (axis === 'x' ? 0.5 : 1),
    transition: motion.hover.transition,
  };
  const press = {
    scale: 0.985,
    transition: { type: 'spring' as const, stiffness: 600, damping: 30 },
  };
  const props = { whileHover: hover, whileTap: press, className: cx('group', className), ...rest };

  if (href) {
    const external = isExternalHref(href);
    return (
      <MotionLink
        href={localize(href)}
        {...(external && { target: '_blank', rel: 'noopener noreferrer' })}
        {...props}
      >
        {children}
      </MotionLink>
    );
  }

  const Component = MOTION_TAGS[as];
  return <Component {...props}>{children}</Component>;
}
